<template>
  <div>
    <div class="link-existing-conversations-panel__search">
      <el-input
        ref="searchRef"
        v-model="searchQuery"
        placeholder="Search conversations (e.g. #123, help...)"
      />
    </div>
    <div class="link-existing-conversations-panel__results">
      <loading-placeholder v-if="isSearching" :lines="20" class="link-existing-conversations-panel__results__searching"/>
      <template v-else>
        <div
          v-if="searchEmpty"
          class="link-existing-conversations-panel__results__hasnt-searched"
        >Your search results will display here.</div>
        <div
          v-else-if="showNoResults"
          class="link-existing-conversations-panel__results__no-results"
        >
          <h5>No Search Results</h5>
          <p>Check if your conversation is already linked or search again.</p>
        </div>
        <div v-else class="link-existing-conversations-panel__results-list">
          <div v-for="(conversation, index) in matchingConversationsToDisplay" :key="index">
            <div
              v-if="showMailboxName(index)"
              class="link-existing-conversations-panel__results-list__row-entity-name"
            >{{conversation.mailbox.name}}</div>
            <div
              v-if="!conversationAlreadyLinked(conversation.id)"
              class="link-existing-conversations-panel__results-list__row-item"
            >
              <quick-add-button
                class="link-existing-conversations-panel__results-list__row-item__add-button"
                title="Add linked conversation"
                @click="linkSelectedConversation(conversation.id)"
              />
              <div
                :class="{'link-existing-conversations-panel__results-list__row-item-content-inactive' : !conversation.isActive }"
                class="link-existing-conversations-panel__results-list__row-item-content"
              >
                <span v-html="conversation.title" />
                <div v-if="showDescription(conversation)" v-html="conversation.description" />
              </div>
            </div>
          </div>
          <div class="link-existing-conversations-panel__results-footer">
            <dd-button type="flat" size="large" @click="$emit('canceled')">Close</dd-button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { debounce } from 'lodash-es'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import {
  ILinkedItemForListing,
  ISearchConversationResult,
} from '@/api/types/response.types'

/* API */
import SearchAPI from '@/api/modules/search.api'

export default defineComponent({
  name: 'LinkExistingConversationsPanel',

  props: {
    currentlyLinkedConversations: {
      type: Array as () => ILinkedItemForListing[],
      required: true,
    },
  },

  emits: ['canceled', 'selected-existing-conversation-id', 'visible'],

  data() {
    return {
      isSearching: false,
      searchQuery: '',
      debounceGetResults: null as any,
      matchingConversations: [] as ISearchConversationResult[],
      selectedConversationIds: [] as number[],
      searchedOnce: false,
      minSearchLength: 2,
    }
  },

  computed: {
    matchingConversationsToDisplay(): ISearchConversationResult[] {
      const result = this.sortByMailbox(this.matchingConversations)
      return result
    },
    searchEmpty(): boolean {
      return this.searchQuery.length < this.minSearchLength
    },
    noMatchingConversations(): boolean {
      return this.matchingConversationsToDisplay.length === 0
    },
    showNoResults(): boolean {
      return (
        this.noMatchingConversations &&
        this.searchedOnce &&
        this.searchQuery.length >= this.minSearchLength
      )
    },
  },

  watch: {
    async searchQuery() {
      if (this.searchQuery.length >= 2) {
        await this.debounceGetResults()
        // @ts-ignore
        if (this.$refs.dropdownRef) {
          // @ts-ignore
          this.$refs.dropdownRef.$emit('visible', true)
        }
      } else {
        this.matchingConversations = [] as ISearchConversationResult[]
      }
    },
  },

  mounted() {
    // @ts-ignore
    this.$refs.searchRef.focus()
  },

  created() {
    this.debounceGetResults = debounce(this.getSearchConversationResults, 300)
  },

  methods: {
    // Show mailbox name if the indexed item has a different mailbox than its predecessor
    showMailboxName(index: number): boolean {
      if (index === 0) {
        return true
      }

      if (
        this.matchingConversationsToDisplay[index - 1].mailbox.id !==
        this.matchingConversationsToDisplay[index].mailbox.id
      ) {
        return true
      }
    },

    sortByMailbox(conversations: ISearchConversationResult[]) {
      conversations.sort(
        (a: ISearchConversationResult, b: ISearchConversationResult) => {
          if (a.mailbox.name.toLowerCase() < b.mailbox.name.toLowerCase()) {
            return -1
          }
          if (a.mailbox.name.toLowerCase() > b.mailbox.name.toLowerCase()) {
            return 1
          }
          if (a.isActive && !b.isActive) {
            return -1
          }
          if (!a.isActive && b.isActive) {
            return 1
          }
          return 0
        },
      )
      return conversations
    },

    // Only show description if there is a text match
    showDescription(conversation: ISearchConversationResult) {
      return conversation.description.includes('<span class="highlight">')
    },

    conversationAlreadyLinked(id: number): boolean {
      return this.currentlyLinkedConversations.some(t => t.id === id)
    },

    async getSearchConversationResults() {
      if (this.searchQuery === '') {
        return
      }
      this.isSearching = true

      let result = await SearchAPI.post_Conversations(this.searchQuery)

      // Scrub away already linked conversations, if matched...
      result = result.filter(t => !this.conversationAlreadyLinked(t.id))

      this.matchingConversations = result
      this.isSearching = false
      this.searchedOnce = true
      this.selectedConversationIds = []
    },

    linkSelectedConversation(id: number) {
      this.$emit('selected-existing-conversation-id', id)
    },
  },
})
</script>

<style lang="scss">
@mixin result-row-position {
  position: relative;
  width: calc(100% + 60px);
  left: -30px;
}

.link-existing-conversations-panel {
  &__search {
    position: absolute;
    width: 100%;
    z-index: 2;
    padding: 20px 30px;
    background: var(--color-background-secondary);
    border-bottom: solid 1px $light-blue-grey-two;
    left: 0;
    top: 86px;
  }

  &__results {
    padding-top: 45px;

    &__searching {
      padding-top: 20px;
    }

    &__hasnt-searched {
      padding-top: 20px;
      font-size: 15px;
      color: var(--color-text-third);
    }

    &__no-results {
      padding-top: 20px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      > h5 {
        color: var(--color-text-third);
      }

      > p {
        color: var(--color-text-third);
      }
    }

    &-list {
      padding-bottom: 80px;

      &__row-entity-name {
        padding: 6px 30px;
        border-bottom: solid 1px $light-blue-grey-two;
        @include result-row-position;
        @include lettering--h5;
      }

      &__row-item {
        background-color: var(--color-general-white);
        display: flex;
        align-items: flex-start;
        padding: 10px 30px;
        border-bottom: solid 1px $light-blue-grey-two;
        @include result-row-position;

        &__add-button {
          margin-top: 3px;
        }

        &-content {
          padding-left: 10px;
          /* Ref Number & Subject */
          > span {
            display: inline-block;
            font-size: 14px;
            line-height: 18px;
            font-weight: 500;
          }
          /* Description */
          > div {
            > * {
              font-size: 14px;
              line-height: 18px;
            }
          }
          &-inactive {
            opacity: 0.5;
          }
        }
      }
    }

    &-footer {
      display: flex;
      justify-content: flex-start;
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      padding: 20px 30px;
      background-color: var(--color-general-white);
      border-top: solid 1px $silver-two;
      /* Above result item checkboxes */
      z-index: 2;
    }
  }
}
</style>
